import { csvCell } from './kit/logic';

export interface ParentChildCsvSpec<P, C> {
  // Header cells, already escaped with csvCell.
  header: string[];
  parents: readonly P[];
  // The children to list under a parent, already filtered the way the table filters them.
  children: (parent: P) => readonly C[];
  // A parent or child may write several lines (This Period / Last Year / Change with YOY), each a list of escaped cells.
  parentLines: (parent: P) => string[][];
  childLines: (child: C, parent: P) => string[][];
  // When set, every line starts with a cell naming its row type, so a spreadsheet can filter to one grain.
  rowType?: { header: string; parent: string; child: string };
}

/**
 * A CSV that lists each parent row followed directly by its child rows -- a product, then its SKUs.
 * Parent rows keep their own totals, so a sum over the whole file counts every child twice: filter
 * on the row type first.
 */
export function buildParentChildCsv<P, C>({ header, parents, children, parentLines, childLines, rowType }: ParentChildCsvSpec<P, C>): string {
  const typed = (label: string) => (cells: string[]) => (rowType ? [csvCell(label, true), ...cells] : cells);
  const headerCells = rowType ? [csvCell(rowType.header, true), ...header] : header;

  const body = parents.flatMap((parent) => [
    ...parentLines(parent).map(typed(rowType?.parent ?? '')),
    ...children(parent).flatMap((child) => childLines(child, parent).map(typed(rowType?.child ?? ''))),
  ]);

  return [headerCells, ...body].map((cells) => cells.join(',')).join('\r\n');
}
